import type { QueryReturnValue } from "@reduxjs/toolkit/dist/query/baseQueryTypes";
import isEmpty from "lodash/isEmpty";
import { logErrorToSentry } from "@litres/utils";
import type { MaybePromise } from "@reduxjs/toolkit/dist/query/tsHelpers";
import type {
  AuthorDetailedSerializer,
  CollectionWithRubricsSerializer,
  GenreFullSerializer,
  TagLightSerializer,
  ArtShortSerializer,
  TDashboardContent,
  IDashboardBlock,
  IDashboardResponse,
  IApiResponse,
  DashboardGenreBlock,
  DashboardArtListBlock,
  AdvertisingTypes,
  CustomBlockArtSerializer,
  RandomQuoteSerializer,
} from "@litres-libs/types";
import { DashboardBlockTypeEnum } from "@litres-libs/types";
import { rtkqApi } from "../api/rtkqApi";
import { TagsType } from "../constants";
import type { RootState } from "../../store";

const getContentType = (
  data:
    | AuthorDetailedSerializer[]
    | CollectionWithRubricsSerializer[]
    | GenreFullSerializer[]
    | TagLightSerializer[]
    | CustomBlockArtSerializer[]
    | AdvertisingTypes.BannerResponse[]
    | ArtShortSerializer
    | RandomQuoteSerializer[],
  blockData: TDashboardContent,
  reachableBanners: AdvertisingTypes.BannerResponse[]
): IDashboardBlock => {
  switch (blockData.type) {
    case DashboardBlockTypeEnum.authorsSlider:
      return {
        authors: data as AuthorDetailedSerializer[],
      };
    case DashboardBlockTypeEnum.editorialCollections:
    case DashboardBlockTypeEnum.collectionsSlider:
      return {
        collections: data as CollectionWithRubricsSerializer[],
      };
    case DashboardBlockTypeEnum.genreSlider:
    case DashboardBlockTypeEnum.genresList:
    case DashboardBlockTypeEnum.genresSliderSecondary:
      return {
        genres: data as GenreFullSerializer[],
      };
    case DashboardBlockTypeEnum.tagsList:
      return {
        tags: data as TagLightSerializer[],
      };
    case DashboardBlockTypeEnum.banners:
      return {
        banners: reachableBanners as AdvertisingTypes.BannerResponse[],
      };
    case DashboardBlockTypeEnum.nextArtInSeries:
      return {
        art: data as ArtShortSerializer,
      };
    case DashboardBlockTypeEnum.quotesSlider:
      return {
        quotes: data as RandomQuoteSerializer[],
      };
    default:
      return {
        arts: data as CustomBlockArtSerializer[],
      };
  }
};

const checkIfImageIsReachable = async (imageUrl: string) => {
  return new Promise(resolve => {
    fetch(imageUrl, { method: "HEAD" })
      .then(response => {
        resolve(response.ok);
      })
      .catch(() => {
        resolve(false);
      });
  });
};

export const dashboardApi = rtkqApi.injectEndpoints({
  endpoints: build => ({
    getDashboard: build.query<
      IDashboardResponse,
      {
        dashboardSlug: string;
      }
    >({
      query: ({ dashboardSlug }) => ({
        url: `/foundation/api/dashboards/${dashboardSlug}`,
      }),
      transformResponse: (response: IApiResponse<IDashboardResponse>) => {
        return response.payload.data;
      },
    }),

    getHomepageDashboard: build.query<IDashboardResponse, { isPDA?: boolean }>({
      async queryFn({ isPDA }, _queryApi, _extraOptions, fetchWithBQ) {
        const homePageMarkupResult: QueryReturnValue<any> = await fetchWithBQ({
          url: "/foundation/api/homepage",
          headers: {
            "Accept-Version": 2,
          },
          params: {
            is_for_pda: isPDA,
          },
        });
        const homePageMarkUpResponse = homePageMarkupResult.data;

        // если ошибка запрсоа разметки, логируем в сентри и отдаем фолбэк
        if (
          !homePageMarkUpResponse ||
          homePageMarkupResult.error ||
          isEmpty(homePageMarkUpResponse.payload.data[0].content)
        ) {
          logErrorToSentry("HomePage: tabs content is empty", {
            contextProperties: ["params", {}],
            tags: {
              type: "critical",
            },
            logLevel: "fatal",
          });
        }
        return { data: homePageMarkUpResponse?.payload.data[0] };
      },
    }),

    getContentForPageWithDashboards: build.query<
      IDashboardBlock,
      { item: TDashboardContent }
    >({
      async queryFn(content, _queryApi, _extraOptions, fetchWithBQ) {
        try {
          const { item } = content;
          let params = {};

          //@ts-ignore
          if (item?.page === "homepage" && !item.type.includes("banner")) {
            const state = _queryApi.getState() as RootState;
            const varioqubFlags = state.foundationUser.varioqubTests.flags;

            const withRandomizeArtSetAB =
              varioqubFlags["with_randomize"] === "true";

            if (withRandomizeArtSetAB) {
              params = { with_randomize_content_test: true };
            }
          }

          // за баннерами идем в рекламные сервисы
          const urlPrefix =
            item.type === DashboardBlockTypeEnum.banners
              ? "/advertising"
              : "/foundation";
          const promises = [];
          // запрос данных для блока
          const contentBlockResult: MaybePromise<QueryReturnValue<any>> =
            fetchWithBQ({
              url: urlPrefix.concat(
                item.type === DashboardBlockTypeEnum.genreBlock
                  ? (item as DashboardGenreBlock).top_arts_url
                  : (item as DashboardArtListBlock).content_url
              ),
              params,
              headers: {
                "accept-version": "2",
              },
            });
          promises.push(contentBlockResult);

          let subgenres = undefined;
          const genreUrl = (item as DashboardGenreBlock).additional_genres_url;
          // для типа блока genreBlock запрашиваем список жанров
          // (отображаются чипсами над слайдером с артами)
          if (item.type === DashboardBlockTypeEnum.genreBlock && genreUrl) {
            const subgenresDataResult: MaybePromise<QueryReturnValue<any>> =
              fetchWithBQ({
                url: `/foundation${genreUrl}`,
              });
            promises.push(subgenresDataResult);
          }
          const [contentBlockResultData, subgenresDataResult] =
            await Promise.all(promises);

          if (subgenresDataResult?.data?.payload?.data) {
            subgenres = subgenresDataResult?.data?.payload?.data;
          }

          const reachableBanners = [];
          if (item.type === DashboardBlockTypeEnum.banners) {
            for (const banner of contentBlockResultData.data.payload.data) {
              const isReachable = await checkIfImageIsReachable(
                banner?.image_url || ""
              );
              if (isReachable) {
                reachableBanners.push(banner);
              }
            }
          }

          // маппинг полученных данных на объект IDashboardBlock
          const data = contentBlockResultData.data
            ? getContentType(
                contentBlockResultData.data.payload.data,
                item,
                reachableBanners
              )
            : {};
          return {
            data: {
              ...data,
              subgenres,
              recommendationModelVersion:
                contentBlockResultData?.data?.payload
                  ?.recommendation_model_version,
            },
          };
        } catch (err) {
          console.log(err);
          return { data: {} };
        }
      },
      providesTags: result =>
        result?.arts
          ? [
              ...result.arts.map(({ id }) => ({
                type: TagsType.ART_SHORT_LIST as const,
                id,
              })),
              TagsType.ART_SHORT_LIST,
            ]
          : result?.art
            ? [
                {
                  type: TagsType.ART_SHORT_LIST as const,
                  id: result.art.id,
                },
              ]
            : result?.quotes
              ? [
                  ...result.quotes.map(({ art_id: id }) => ({
                    type: TagsType.ART_SHORT_LIST as const,
                    id,
                  })),
                  TagsType.ART_SHORT_LIST,
                ]
              : [],
    }),
  }),
});
